import React, { useEffect, useId, useRef } from 'react';
import { Link } from 'react-router-dom';
import Reveal from './Reveal';
import { prefiereQuieto, useEnVista } from '../utils/motion';
import { useAuthStore } from '../store/auth';

// Sección "Invita y gana" de la landing. Describe el programa REAL (api/controllers/ReferidosController.php):
//  - enlace /registro?ref=CODIGO (el código y el enlace están en Perfil, «Invita y gana»);
//  - referidor normal: 1 mes de Pro cuando su invitado hace su PRIMER pago;
//  - referidor contador (es_contador): 20 % de cada pago del referido; pago manual.
const FOCUS = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900';

// Animación solo con transform/opacity/stroke: el escenario tiene aspect-ratio fijo (sin CLS).
// Fases: previa (aún no se ve) -> juega (al entrar en pantalla) | quieta (prefers-reduced-motion: estado final).
const CSS = `
.lr-stage[data-fase='previa'] .lr-mask { stroke-dashoffset: 1; }
.lr-stage[data-fase='previa'] .lr-coin, .lr-stage[data-fase='previa'] .lr-badge { opacity: 0; }
.lr-stage[data-fase='quieta'] .lr-coin { opacity: 0; }
.lr-mask { stroke-dasharray: 1; stroke-dashoffset: 0; }
@keyframes lr-draw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
@keyframes lr-coin { 0% { opacity: 0; } 12% { opacity: 1; } 85% { opacity: 1; } 100% { opacity: 0; } }
@keyframes lr-pop { 0% { opacity: 0; transform: translate3d(0, 6px, 0) scale(.6); } 60% { opacity: 1; transform: translate3d(0, 0, 0) scale(1.08); } 100% { opacity: 1; transform: none; } }
@keyframes lr-ring { 0% { opacity: .55; transform: scale(1); } 100% { opacity: 0; transform: scale(1.5); } }
.lr-stage[data-fase='juega'] .lr-mask { animation: lr-draw 1000ms var(--lp-ease-in-out, ease-in-out) 150ms both; }
.lr-stage[data-fase='juega'] .lr-coin { animation: lr-coin 1100ms linear 1250ms both; }
.lr-stage[data-fase='juega'] .lr-badge { animation: lr-pop 520ms var(--lp-ease-out, ease-out) 2250ms both; }
.lr-stage[data-fase='juega'] .lr-ring { animation: lr-ring 900ms var(--lp-ease-out, ease-out) 2250ms both; }
.lr-ring { opacity: 0; }
.lr-badge { transform-origin: left bottom; }
@media (prefers-reduced-motion: reduce) {
  .lr-stage .lr-mask, .lr-stage .lr-badge, .lr-stage .lr-ring { animation: none !important; }
  .lr-stage .lr-mask { stroke-dashoffset: 0 !important; }
  .lr-stage .lr-badge { opacity: 1 !important; transform: none !important; }
  .lr-stage .lr-coin { display: none; }
}
`;

const IDA = 'M73 54 C 110 8, 190 8, 227 54';
const VUELTA = 'M227 54 C 190 100, 110 100, 73 54';

function Persona({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true" focusable="false">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21c0-4.4 3.6-7 8-7s8 2.6 8 7z" />
    </svg>
  );
}

function Conexion() {
  const raiz = useRef(null);
  const movimiento = useRef(null);
  const idMascara = useId().replace(/:/g, '');
  const quieta = prefiereQuieto();
  const visto = useEnVista(raiz, 0.5);
  const fase = quieta ? 'quieta' : visto ? 'juega' : 'previa';

  useEffect(() => {
    if (fase !== 'juega') return;
    const m = movimiento.current;
    if (m && typeof m.beginElementAt === 'function') {
      try { m.beginElementAt(1.25); } catch { /* sin SMIL: la moneda solo hace fade */ }
    }
  }, [fase]);

  return (
    <div ref={raiz} data-fase={fase} aria-hidden="true"
      className="lr-stage relative mx-auto w-full max-w-[360px] select-none" style={{ aspectRatio: '300 / 124' }}>
      <svg viewBox="0 0 300 124" className="absolute inset-0 h-full w-full overflow-visible" focusable="false">
        <defs>
          <linearGradient id={`${idMascara}g`} x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#6366F1" />
            <stop offset="1" stopColor="#10B981" />
          </linearGradient>
          <mask id={`${idMascara}m`} maskUnits="userSpaceOnUse" x="0" y="0" width="300" height="124">
            <path className="lr-mask" d={IDA} pathLength="1" fill="none" stroke="#fff" strokeWidth="8" strokeLinecap="round" />
          </mask>
        </defs>
        <path d={IDA} fill="none" stroke={`url(#${idMascara}g)`} strokeWidth="3" strokeLinecap="round" strokeDasharray="2 8" mask={`url(#${idMascara}m)`} />
        <g className="lr-coin">
          <g>
            <circle r="11" fill="#FBBF24" stroke="#F59E0B" strokeWidth="2" />
            <text y="4" textAnchor="middle" fontSize="12" fontWeight="800" fill="#92400E">$</text>
            <animateMotion ref={movimiento} dur="1.1s" begin="indefinite" fill="freeze" path={VUELTA} calcMode="spline" keyTimes="0;1" keySplines="0.4 0 0.2 1" />
          </g>
        </g>
      </svg>

      {/* Burbujas de avatar (en % del escenario: 45/300 y 255/300 de ancho, 54/124 de alto) */}
      <div className="absolute flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-indigo-600 text-white shadow-lg shadow-indigo-500/30 ring-4 ring-white dark:ring-slate-900" style={{ left: '15%', top: '43.55%' }}>
        <span className="lr-ring absolute inset-0 rounded-full bg-emerald-400" />
        <Persona className="h-8 w-8" />
      </div>
      <div className="absolute flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/30 ring-4 ring-white dark:ring-slate-900" style={{ left: '85%', top: '43.55%' }}>
        <Persona className="h-8 w-8" />
      </div>
      <span className="absolute -translate-x-1/2 text-xs font-semibold text-slate-600 dark:text-slate-300" style={{ left: '15%', top: '72%' }}>Tú</span>
      <span className="absolute -translate-x-1/2 whitespace-nowrap text-xs font-semibold text-slate-600 dark:text-slate-300" style={{ left: '85%', top: '72%' }}>Tu invitado</span>
      <span className="lr-badge absolute whitespace-nowrap rounded-full bg-gradient-to-r from-indigo-600 to-emerald-500 px-3 py-1 text-xs font-bold text-white shadow-md" style={{ left: '2%', top: '0%' }}>+1 mes de Pro</span>
    </div>
  );
}

export default function LandingReferidos({ trialDias = 14 }) {
  const autenticado = useAuthStore((s) => s.isAuthenticated);

  const pasos = [
    { n: '1', title: 'Comparte tu enlace', desc: 'Lo encuentras en tu Perfil, en «Invita y gana»: es el enlace de registro de MONIMEX con tu código personal.' },
    { n: '2', title: 'Tu invitado se registra', desc: `Crea su cuenta con tu enlace y empieza con sus ${trialDias} días de Pro, como todos.` },
    { n: '3', title: 'Ganas 1 mes de Pro', desc: 'Cuando haga su primer pago, se te suma 1 mes de Pro gratis, a partir de la fecha en que vence tu Pro actual.' },
  ];

  return (
    <section id="invita" aria-labelledby="invita-titulo" className="py-20 px-4 scroll-mt-16">
      <style>{CSS}</style>
      <div className="max-w-5xl mx-auto rounded-3xl border border-indigo-100 dark:border-indigo-400/20 bg-gradient-to-br from-indigo-50 via-white to-emerald-50 dark:from-indigo-950/50 dark:via-slate-900 dark:to-emerald-950/30 px-5 sm:px-10 py-12">
        <Reveal><div className="text-center mb-8">
          <p className="inline-block text-xs font-bold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 mb-3">Invita y gana</p>
          <h2 id="invita-titulo" className="text-3xl sm:text-4xl font-black mb-3 text-slate-900 dark:text-white">Invita a alguien y gana 1 mes de Pro</h2>
          <p className="text-slate-600 dark:text-slate-300 text-lg max-w-2xl mx-auto">
            Cada persona que se registre con tu enlace y haga su primer pago te regala un mes de Pro. Sin código de cupón y sin trámites.
          </p>
        </div></Reveal>

        <Conexion />

        <ol className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {pasos.map((p, i) => (
            <li key={p.n}>
              <Reveal delay={i * 100} className="h-full">
                <div className="h-full rounded-2xl bg-white/80 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700 p-5 text-center md:text-left">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-600 to-emerald-500 text-white font-black flex items-center justify-center mx-auto md:mx-0 mb-3" aria-hidden="true">{p.n}</div>
                  <h3 className="font-bold text-slate-900 dark:text-white mb-1">{p.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            </li>
          ))}
        </ol>

        <div className="mt-8 text-center">
          {autenticado ? (
            <Link to="/perfil" className={`inline-flex items-center justify-center gap-2 min-w-[17rem] bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-7 py-3.5 rounded-xl text-base lp-press shadow-lg shadow-indigo-500/25 ${FOCUS}`}>
              Ver mi enlace de invitación
            </Link>
          ) : (
            <>
              <Link to="/registro" className={`inline-flex items-center justify-center gap-2 min-w-[17rem] bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-7 py-3.5 rounded-xl text-base lp-press shadow-lg shadow-indigo-500/25 ${FOCUS}`}>
                Crea tu cuenta y obtén tu enlace
              </Link>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-3">
                ¿Ya tienes cuenta?{' '}
                <Link to="/perfil" className={`font-semibold text-indigo-700 dark:text-indigo-300 underline rounded ${FOCUS}`}>Tu enlace está en tu Perfil</Link>.
              </p>
            </>
          )}
        </div>

        <p className="text-xs text-slate-600 dark:text-slate-400 text-center max-w-2xl mx-auto mt-6 leading-relaxed">
          Se gana 1 mes por cada invitado que hace su primer pago. Si tu Pro no tiene fecha de vencimiento, no hay nada que extender.
          Si tu cuenta es de contador, en lugar del mes gratis recibes el 20 % de cada pago de tus referidos, mes con mes; esa comisión se liquida de forma manual.
        </p>
      </div>
    </section>
  );
}
